"use client";

import { useState, useEffect, useCallback, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Home,
  FolderOpen,
  Users,
  Layers,
  Bell,
  Search,
  Building,
  MessageCircle,
  FileText,
  ChevronRight,
  Clock,
  CheckCircle2,
  Plus,
  Globe,
  Lock,
  EyeOff,
  Activity,
  Compass,
  Map,
  Monitor,
  Palette,
  RefreshCw,
  MessageSquare,
  RotateCcw,
  FileEdit,
  LayoutGrid,
  List,
  ArrowRight,
  ArrowUpRight,
  Folder,
  Circle,
  AlertTriangle,
  PenSquare,
  Calendar,
} from "lucide-react";

// Import shared components from production
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/user-avatar";

// Mock UI Mockup Components - Context-appropriate for each designer

// Dieter Rams - Braun T3 pocket radio sketch
function BraunSketchMockup() {
  return (
    <div className="w-full h-full bg-[#f5f5f0] p-3 flex items-center justify-center overflow-hidden">
      {/* Braun T3 pocket radio - clean technical drawing style */}
      <div className="relative">
        {/* Main body */}
        <div className="w-28 h-20 bg-white border border-gray-400 rounded relative">
          {/* Speaker grille - perforated pattern */}
          <div className="absolute top-2 left-2 w-14 h-16 grid grid-cols-6 gap-0.5">
            {[...Array(48)].map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 bg-gray-200 rounded-full" />
            ))}
          </div>
          {/* Volume dial */}
          <div className="absolute top-3 right-3 w-7 h-7 border border-gray-400 rounded-full bg-gray-50 flex items-center justify-center">
            <div className="w-3 h-0.5 bg-gray-400" />
          </div>
          {/* Tuning dial */}
          <div className="absolute bottom-3 right-3 w-5 h-5 border border-gray-400 rounded-full bg-gray-50" />
        </div>
      </div>
    </div>
  );
}

function BraunCalculatorMockup() {
  return (
    <div className="w-full h-full bg-[#f5f5f0] p-2 flex items-center justify-center overflow-hidden">
      {/* Braun ET calculator style - compact version */}
      <div className="w-20 bg-white border-2 border-gray-300 rounded relative p-1.5">
        {/* Display */}
        <div className="h-5 bg-[#1a1a1a] rounded-sm flex items-center justify-end px-1 mb-1.5">
          <span className="text-green-400 font-mono text-[10px]">0.</span>
        </div>
        {/* Button grid - 4 rows */}
        <div className="grid grid-cols-4 gap-0.5">
          {[
            "C",
            "±",
            "%",
            "÷",
            "7",
            "8",
            "9",
            "×",
            "4",
            "5",
            "6",
            "−",
            "1",
            "2",
            "3",
            "+",
          ].map((btn, i) => (
            <div
              key={i}
              className={`h-3.5 rounded-sm flex items-center justify-center text-[5px] font-medium
                ${
                  i < 4
                    ? "bg-gray-200 text-gray-600"
                    : [3, 7, 11, 15].includes(i)
                      ? "bg-orange-400 text-white"
                      : "bg-gray-100 text-gray-800"
                }`}
            >
              {btn}
            </div>
          ))}
        </div>
        {/* Bottom row */}
        <div className="grid grid-cols-4 gap-0.5 mt-0.5">
          <div className="col-span-2 h-3.5 bg-gray-100 rounded-sm flex items-center justify-center text-[5px]">
            0
          </div>
          <div className="h-3.5 bg-gray-100 rounded-sm flex items-center justify-center text-[5px]">
            .
          </div>
          <div className="h-3.5 bg-orange-400 rounded-sm flex items-center justify-center text-[5px] text-white">
            =
          </div>
        </div>
      </div>
    </div>
  );
}

// Massimo Vignelli - NYC Subway Map style
function SubwayMapMockup() {
  return (
    <div className="w-full h-full bg-[#f5f5f0] p-4 flex items-center justify-center overflow-hidden">
      {/* Simplified subway map in Vignelli style - clean, no grid */}
      <svg viewBox="0 0 100 70" className="w-full h-full max-w-[280px]">
        {/* Blue line (A/C/E) - horizontal main line */}
        <path
          d="M 5 35 L 95 35"
          stroke="#0039A6"
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
        />
        {/* Red line (1/2/3) - vertical with 45° turn */}
        <path
          d="M 25 5 L 25 35 L 45 55"
          stroke="#EE352E"
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Green line (4/5/6) - vertical with 45° turn */}
        <path
          d="M 55 5 L 55 35 L 75 55"
          stroke="#00933C"
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Orange line (B/D/F/M) - diagonal */}
        <path
          d="M 40 5 L 40 35 L 60 55 L 85 55"
          stroke="#FF6319"
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Transfer stations - white circles with black border */}
        <circle cx="25" cy="35" r="4" fill="white" stroke="#333" strokeWidth="2" />
        <circle cx="40" cy="35" r="4" fill="white" stroke="#333" strokeWidth="2" />
        <circle cx="55" cy="35" r="4" fill="white" stroke="#333" strokeWidth="2" />
        <circle cx="60" cy="55" r="4" fill="white" stroke="#333" strokeWidth="2" />
      </svg>
    </div>
  );
}

// Susan Kare - Happy Mac pixel art icon
function MacIconsMockup() {
  // 16x16 pixel grid for Happy Mac - 1 = black pixel, 0 = transparent
  const happyMacPixels = [
    [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
    [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    [0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0],
    [0, 1, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0],
    [0, 1, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0],
    [0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0],
    [0, 1, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 0],
    [0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0],
    [0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0],
    [0, 1, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 1, 0],
    [0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0],
    [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  ];

  return (
    <div className="w-full h-full bg-[#f5f5f0] p-4 flex items-center justify-center overflow-hidden">
      {/* Pixel grid representation */}
      <div className="flex flex-col gap-[2px]">
        {happyMacPixels.map((row, y) => (
          <div key={y} className="flex gap-[2px]">
            {row.map((pixel, x) => (
              <div
                key={x}
                className={`w-3 h-3 ${pixel ? "bg-gray-800" : "bg-transparent"}`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Mock updates that animate in
interface MockUpdate {
  id: string;
  author: {
    name: string;
    initials: string;
  };
  teamName: string;
  projectName?: string;
  text: string;
  timeAgo: string;
  reactions: { emoji: string; count: number }[];
  commentCount: number;
  mockups?: React.ComponentType[];
}

const mockUpdates: MockUpdate[] = [
  {
    id: "1",
    author: { name: "Dieter Rams", initials: "DR" },
    teamName: "Industrial Design",
    projectName: "Braun Products",
    text: "Sharing explorations for the T3 pocket radio and ET66 calculator. Less, but better—every element serves a purpose.",
    timeAgo: "2 hours ago",
    reactions: [
      { emoji: "👍", count: 4 },
      { emoji: "✨", count: 2 },
    ],
    commentCount: 3,
    mockups: [BraunSketchMockup, BraunCalculatorMockup],
  },
  {
    id: "2",
    author: { name: "Massimo Vignelli", initials: "MV" },
    teamName: "Wayfinding",
    projectName: "NYC Subway Map",
    text: "Revised the diagram to emphasize clarity over geographic accuracy. The 45° angles create a consistent visual rhythm. Sharing latest iteration for feedback.",
    timeAgo: "5 hours ago",
    reactions: [
      { emoji: "🗺️", count: 3 },
      { emoji: "💡", count: 2 },
    ],
    commentCount: 5,
    mockups: [SubwayMapMockup],
  },
  {
    id: "3",
    author: { name: "Susan Kare", initials: "SK" },
    teamName: "Interface Design",
    projectName: "Macintosh Icons",
    text: "Exploring the Happy Mac icon on the pixel grid. Every square matters at 16x16—this little face will greet millions of users on startup.",
    timeAgo: "Yesterday",
    reactions: [
      { emoji: "🖥️", count: 4 },
      { emoji: "❤️", count: 3 },
    ],
    commentCount: 8,
    mockups: [MacIconsMockup],
  },
];

// Mock projects data
const mockProjects = [
  {
    id: "1",
    name: "Braun Products",
    team: "Industrial Design",
    lead: { name: "Dieter Rams", initials: "DR" },
    updates: 12,
    status: "active",
  },
  {
    id: "2",
    name: "NYC Subway Map",
    team: "Wayfinding",
    lead: { name: "Massimo Vignelli", initials: "MV" },
    updates: 8,
    status: "active",
  },
  {
    id: "3",
    name: "Macintosh Icons",
    team: "Interface Design",
    lead: { name: "Susan Kare", initials: "SK" },
    updates: 15,
    status: "shipped",
  },
  {
    id: "4",
    name: "IBM Logo Redesign",
    team: "Brand Identity",
    lead: { name: "Paul Rand", initials: "PR" },
    updates: 6,
    status: "active",
  },
  {
    id: "5",
    name: "I Love NY Campaign",
    team: "Brand Identity",
    lead: { name: "Milton Glaser", initials: "MG" },
    updates: 4,
    status: "shipped",
  },
];

// Mock teams data - enhanced to match production
const mockTeams = [
  {
    id: "1",
    name: "Industrial Design",
    description: "Creating timeless products through form and function",
    members: [
      { name: "Dieter Rams", initials: "DR" },
      { name: "Charles Eames", initials: "CE" },
    ],
    projects: 3,
    activeProjects: 2,
    gradient: { from: "#6366f1", to: "#8b5cf6" },
    icon: "Compass",
    membershipType: "open" as const,
  },
  {
    id: "2",
    name: "Wayfinding",
    description: "Making complex systems navigable and intuitive",
    members: [
      { name: "Massimo Vignelli", initials: "MV" },
      { name: "Paula Scher", initials: "PS" },
    ],
    projects: 2,
    activeProjects: 1,
    gradient: { from: "#0ea5e9", to: "#6366f1" },
    icon: "Map",
    membershipType: "closed" as const,
  },
  {
    id: "3",
    name: "Interface Design",
    description: "Crafting pixel-perfect digital experiences",
    members: [
      { name: "Susan Kare", initials: "SK" },
      { name: "Jony Ive", initials: "JI" },
    ],
    projects: 4,
    activeProjects: 3,
    gradient: { from: "#ec4899", to: "#f43f5e" },
    icon: "Monitor",
    membershipType: "open" as const,
  },
  {
    id: "4",
    name: "Brand Identity",
    description: "Building memorable visual systems",
    members: [
      { name: "Paul Rand", initials: "PR" },
      { name: "Milton Glaser", initials: "MG" },
    ],
    projects: 5,
    activeProjects: 2,
    gradient: { from: "#f59e0b", to: "#ef4444" },
    icon: "Palette",
    membershipType: "private" as const,
  },
];

// Mock workflows data - enhanced to match production
const mockWorkflows = [
  {
    id: "1",
    title: "Daily Standup",
    type: "standup" as const,
    status: "active" as const,
    team: "All Teams",
    teamId: "all",
    owner: { name: "Paula Scher", initials: "PS" },
    schedule: "Every day at 9am",
    nextRun: "Tomorrow",
    participants: 8,
    lastActivity: "Today",
  },
  {
    id: "2",
    title: "Design Review",
    type: "crit" as const,
    status: "active" as const,
    team: "Interface Design",
    teamId: "3",
    owner: { name: "Susan Kare", initials: "SK" },
    schedule: "Wednesdays at 2pm",
    nextRun: "In 2 days",
    participants: 4,
    lastActivity: "2 days ago",
  },
  {
    id: "3",
    title: "Weekly Rollup",
    type: "rollup" as const,
    status: "published" as const,
    team: "All Teams",
    teamId: "all",
    owner: { name: "Dieter Rams", initials: "DR" },
    schedule: "Fridays at 5pm",
    nextRun: "In 4 days",
    participants: 12,
    lastActivity: "Last week",
  },
  {
    id: "4",
    title: "Sprint Retrospective",
    type: "retro" as const,
    status: "draft" as const,
    team: "Industrial Design",
    teamId: "1",
    owner: { name: "Dieter Rams", initials: "DR" },
    schedule: "Bi-weekly",
    nextRun: "Next week",
    participants: 5,
    lastActivity: "3 days ago",
  },
];

// Mock notifications data
const mockNotifications = [
  {
    id: "1",
    type: "comment",
    actor: { name: "Massimo Vignelli", initials: "MV" },
    action: "commented on",
    target: "Braun Products",
    time: "10 min ago",
    unread: true,
  },
  {
    id: "2",
    type: "reaction",
    actor: { name: "Susan Kare", initials: "SK" },
    action: "reacted to",
    target: "your update",
    time: "1 hour ago",
    unread: true,
  },
  {
    id: "3",
    type: "mention",
    actor: { name: "Paul Rand", initials: "PR" },
    action: "mentioned you in",
    target: "IBM Logo Redesign",
    time: "3 hours ago",
    unread: true,
  },
  {
    id: "4",
    type: "shipped",
    actor: { name: "Milton Glaser", initials: "MG" },
    action: "shipped",
    target: "I Love NY Campaign",
    time: "Yesterday",
    unread: false,
  },
  {
    id: "5",
    type: "comment",
    actor: { name: "Dieter Rams", initials: "DR" },
    action: "replied to",
    target: "your comment",
    time: "2 days ago",
    unread: false,
  },
];

// Navigation items
type NavItem = "home" | "feed" | "projects" | "teams" | "workflows" | "notifications";

const mainNavItems: { id: NavItem; label: string; icon: typeof Home }[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "projects", label: "Projects", icon: FolderOpen },
  { id: "teams", label: "Teams", icon: Users },
  { id: "workflows", label: "Workflows", icon: Layers },
];

// Demo Sidebar - Desktop version (left side)
function DemoSidebar({
  activeNav,
  setActiveNav,
}: {
  activeNav: NavItem;
  setActiveNav: (nav: NavItem) => void;
}) {
  const unreadCount = mockNotifications.filter((n) => n.unread).length;

  return (
    <div className="hidden sm:block fixed top-1/2 -translate-y-1/2 left-4 z-40 w-16 transition-all duration-300 ease-in-out">
      <div className="bg-white/95 backdrop-blur-md shadow-xl rounded-2xl border border-gray-100 flex flex-col overflow-hidden">
        {/* Header - Collapsed State */}
        <div className="p-2 border-b border-gray-100">
          <div className="flex flex-col items-center gap-2">
            {/* Org Icon */}
            <Button
              variant="ghost"
              size="sm"
              className="w-10 h-10 p-0 rounded-lg"
              title="Acme Design"
            >
              <div className="w-7 h-7 rounded bg-gray-900 flex items-center justify-center">
                <Building className="h-4 w-4 text-white" />
              </div>
            </Button>
            {/* Search */}
            <Button
              variant="ghost"
              size="sm"
              className="w-10 h-10 p-0 rounded-lg"
              title="Search"
            >
              <Search className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Navigation Items - Collapsed */}
        <nav className="p-2 flex-1">
          {/* Main Navigation */}
          <div className="space-y-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeNav === item.id;
              return (
                <Button
                  key={item.id}
                  variant={isActive ? "secondary" : "ghost"}
                  className={`w-full justify-center px-0 ${
                    isActive ? "bg-gray-100" : ""
                  } rounded-lg`}
                  title={item.label}
                  onClick={() => setActiveNav(item.id)}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                </Button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-gray-200"></div>

          {/* Notifications */}
          <div className="space-y-1">
            <Button
              variant={activeNav === "notifications" ? "secondary" : "ghost"}
              className={`w-full justify-center px-0 rounded-lg relative ${
                activeNav === "notifications" ? "bg-gray-100" : ""
              }`}
              title="Notifications"
              onClick={() => setActiveNav("notifications")}
            >
              <Bell className="h-5 w-5 flex-shrink-0" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full h-5 min-w-[1.25rem] px-1.5 flex items-center justify-center font-medium">
                  {unreadCount}
                </span>
              )}
            </Button>
          </div>
        </nav>

        {/* User Menu - Collapsed */}
        <div className="p-2 border-t border-gray-100">
          <Button
            variant="ghost"
            className="w-full flex justify-center items-center p-0 h-10 rounded-lg"
            title="Paula Scher"
          >
            <UserAvatar
              displayName="Paula Scher"
              email="paula@acme.com"
              size="sm"
            />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Mobile Bottom Nav
function MobileBottomNav({
  activeNav,
  setActiveNav,
}: {
  activeNav: NavItem;
  setActiveNav: (nav: NavItem) => void;
}) {
  const unreadCount = mockNotifications.filter((n) => n.unread).length;

  return (
    <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 px-2 pb-safe">
      <div className="flex items-center justify-around py-2">
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeNav === item.id;
          return (
            <button
              key={item.id}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                isActive ? "text-gray-900" : "text-gray-500"
              }`}
              onClick={() => setActiveNav(item.id)}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          );
        })}
        <button
          className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors relative ${
            activeNav === "notifications" ? "text-gray-900" : "text-gray-500"
          }`}
          onClick={() => setActiveNav("notifications")}
        >
          <div className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 min-w-[1rem] px-1 flex items-center justify-center font-medium">
                {unreadCount}
              </span>
            )}
          </div>
          <span className="text-[10px] font-medium">Activity</span>
        </button>
      </div>
    </div>
  );
}

// Activity Sidebar - mirrors production styling with QuickAccess + Activity
function DemoActivitySidebar() {
  return (
    <div className="hidden lg:block w-80 flex-shrink-0">
      <div className="sticky top-6 space-y-4">
        {/* Quick Access Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Profile Card */}
          <div className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <UserAvatar
                displayName="Paula Scher"
                email="paula@acme.com"
                size="md"
              />
              <div className="flex-1 min-w-0 text-left">
                <div className="font-semibold text-gray-900 text-sm truncate">
                  Paula Scher
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <span>View profile</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="p-3 space-y-1">
            <div className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              <div className="flex items-center gap-2.5">
                <Users className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700">My Teams</span>
              </div>
              <span className="text-xs text-gray-400">3</span>
            </div>

            <div className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              <div className="flex items-center gap-2.5">
                <FolderOpen className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700">Active Projects</span>
              </div>
              <span className="text-xs text-gray-400">5</span>
            </div>
          </div>
        </div>

        {/* Activity Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Activity</h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Active Now */}
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Active now
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors">
                  <div className="relative">
                    <UserAvatar
                      displayName="Dieter Rams"
                      email="dieter@acme.com"
                      size="sm"
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">Dieter Rams</span>
                </div>
                <div className="flex items-center gap-2 hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors">
                  <div className="relative">
                    <UserAvatar
                      displayName="Milton Glaser"
                      email="milton@acme.com"
                      size="sm"
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-700">Milton Glaser</span>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Today
              </div>
              <div className="space-y-3">
                <div className="hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <UserAvatar
                        displayName="Massimo Vignelli"
                        email="massimo@acme.com"
                        size="xs"
                      />
                    </div>
                    <div>
                      <p className="text-sm text-gray-700 leading-snug">
                        <span className="font-medium">Massimo</span>{" "}
                        <span className="text-gray-500">
                          updated subway map diagram
                        </span>
                      </p>
                      <span className="text-xs text-gray-400">5h ago</span>
                    </div>
                  </div>
                </div>
                <div className="hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <UserAvatar
                        displayName="Susan Kare"
                        email="susan@acme.com"
                        size="xs"
                      />
                    </div>
                    <div>
                      <p className="text-sm text-gray-700 leading-snug">
                        <span className="font-medium">Susan</span>{" "}
                        <span className="text-gray-500">
                          shipped Macintosh icon set
                        </span>
                      </p>
                      <span className="text-xs text-gray-400">Yesterday</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Update Card Component
function UpdateCard({ update, isNew = false }: { update: MockUpdate; isNew?: boolean }) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border p-4 ${
        isNew ? "border-blue-200 animate-slideUp" : "border-gray-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <UserAvatar
          displayName={update.author.name}
          email={`${update.author.initials.toLowerCase()}@acme.com`}
          size="md"
        />

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">
                {update.author.name}
              </div>
              <div className="text-gray-500 text-sm">
                {isNew ? "Just now" : update.timeAgo}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      {(update.teamName || update.projectName) && (
        <div className="flex gap-2 mb-2">
          {update.teamName && (
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
              <Users className="h-3 w-3" />
              <span>{update.teamName}</span>
            </div>
          )}
          {update.projectName && (
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
              <FileText className="h-3 w-3" />
              <span>{update.projectName}</span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="text-gray-800 mb-3">{update.text}</div>

      {/* UI Mockups */}
      {update.mockups && update.mockups.length > 0 && (
        <div
          className={`mb-3 grid gap-2 ${
            update.mockups.length === 1 ? "grid-cols-1" : "grid-cols-2"
          }`}
        >
          {update.mockups.map((Mockup, idx) => (
            <div
              key={idx}
              className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 shadow-sm"
            >
              <Mockup />
            </div>
          ))}
        </div>
      )}

      {/* Reactions and Comments */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1">
          {update.reactions.map((reaction, idx) => (
            <div
              key={idx}
              className="h-7 px-2 rounded-full text-sm bg-gray-100 text-gray-700 flex items-center gap-1"
            >
              <span>{reaction.emoji}</span>
              <span className="text-xs">{reaction.count}</span>
            </div>
          ))}
        </div>
        <div className="h-7 px-2 rounded-full bg-gray-50 text-gray-600 flex items-center gap-1 text-sm">
          <MessageCircle className="h-4 w-4" />
          {update.commentCount > 0 && (
            <span className="text-xs">{update.commentCount}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Home View — mirrors production /home, using the famous-designer cast from
// elsewhere in this demo. Sections: masthead, actions bar, serif hero with
// inline entity links, two-column signals, projects grid, needs attention.

type HomeSegment = { text: string; isLink?: boolean };

function HomeView({ setActiveNav }: { setActiveNav: (nav: NavItem) => void }) {
  const heroSegments: HomeSegment[] = [
    { text: "Since yesterday, " },
    { text: "Dieter Rams", isLink: true },
    { text: " posted new explorations for the " },
    { text: "T3 pocket radio", isLink: true },
    { text: " and " },
    { text: "Massimo Vignelli", isLink: true },
    { text: " shipped the subway map revision on " },
    { text: "NYC Subway Map", isLink: true },
    { text: ". " },
    { text: "Interface Design", isLink: true },
    { text: " hasn't run a retro in over a month — a short one might surface what stalled on the Macintosh icon set." },
  ];

  const plateItems = [
    {
      id: "p1",
      kind: "standup",
      label: "Industrial Design standup",
      sublabel: "Open — not submitted",
    },
    {
      id: "p2",
      kind: "action",
      label: "Review subway map revisions with Massimo",
      sublabel: "Overdue",
      tone: "overdue",
    },
    {
      id: "p3",
      kind: "review",
      label: "Review: Braun ET66 calculator refinements",
      sublabel: "Dieter Rams · 4h ago",
    },
    {
      id: "p4",
      kind: "action",
      label: "Finalize I Love NY campaign asset kit",
      sublabel: "high priority",
      tone: "high",
    },
  ];

  const thisWeek = [
    {
      id: "t1",
      author: "Dieter Rams",
      team: "Industrial Design",
      project: "Braun Products",
      snippet: "Sharing T3 pocket radio + ET66 calculator explorations. Less, but better — every element serves a purpose.",
      ago: "2h ago",
    },
    {
      id: "t2",
      author: "Massimo Vignelli",
      team: "Wayfinding",
      project: "NYC Subway Map",
      snippet: "Revised the diagram to emphasize clarity over geographic accuracy. 45° angles create a consistent rhythm.",
      ago: "5h ago",
    },
    {
      id: "t3",
      author: "Susan Kare",
      team: "Interface Design",
      project: "Macintosh Icons",
      snippet: "Pixel grid exploration for the Happy Mac. Every square matters at 16×16 — this greets millions on startup.",
      ago: "Yesterday",
    },
    {
      id: "t4",
      author: "Paul Rand",
      team: "Brand Identity",
      snippet: "Copy review is blocking the IBM logo rollout. Need @milton to align on the stripe count before end of week.",
      ago: "Yesterday",
      tag: "blocked" as const,
    },
  ];

  const projects = [
    { id: "pr1", name: "Braun Products", team: "Industrial Design", updated: "Today" },
    { id: "pr2", name: "NYC Subway Map", team: "Wayfinding", updated: "Today" },
    { id: "pr3", name: "Macintosh Icons", team: "Interface Design", updated: "Yesterday" },
    { id: "pr4", name: "IBM Logo Redesign", team: "Brand Identity", updated: "Yesterday" },
    { id: "pr5", name: "I Love NY Campaign", team: "Brand Identity", updated: "3d ago" },
    { id: "pr6", name: "Eames Chair Revival", team: "Industrial Design", updated: "9d ago" },
  ];

  const needsAttention = [
    { id: "a1", label: "Eames Chair Revival", sublabel: "9d without an update" },
    { id: "a2", label: "Paul Rand flagged a blocker", sublabel: "Brand Identity · Yesterday" },
    { id: "a3", label: "Industrial Design runs standups Mondays around 10am — make it recurring?", sublabel: "4 in 28 days · not scheduled" },
    { id: "a4", label: "1 overdue action item", sublabel: "On you" },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-12 py-2">
      {/* Masthead */}
      <header className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Tuesday, Apr 23</p>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">Good morning, Paula</h1>
        <p className="text-sm text-gray-500">
          A read on what&rsquo;s moving across{" "}
          <HomeDemoLink>Industrial Design</HomeDemoLink>,{" "}
          <HomeDemoLink>Wayfinding</HomeDemoLink>,{" "}
          <HomeDemoLink>Interface Design</HomeDemoLink>, and{" "}
          <HomeDemoLink>Brand Identity</HomeDemoLink>.
        </p>
      </header>

      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <HomeDemoPill primary icon={Calendar}>Post your Industrial Design standup</HomeDemoPill>
        <HomeDemoPill icon={PenSquare}>Share an update on Braun Products</HomeDemoPill>
        <HomeDemoPill icon={RotateCcw}>Run a retro for Interface Design</HomeDemoPill>
        <HomeDemoPill icon={Folder}>Create a project</HomeDemoPill>
      </div>

      {/* Hero */}
      <p className="font-serif text-xl md:text-2xl leading-[1.55] tracking-tight text-gray-900 max-w-3xl">
        {heroSegments.map((seg, i) =>
          seg.isLink ? (
            <HomeDemoLink key={i} strong>{seg.text}</HomeDemoLink>
          ) : (
            <span key={i}>{seg.text}</span>
          )
        )}
      </p>

      {/* Two-column signals */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <HomeDemoSection title="Waiting on you" note="4 items, 1 overdue." count={plateItems.length}>
          <HomeDemoStack>
            {plateItems.map((item) => (
              <HomeDemoPlateRow key={item.id} item={item} />
            ))}
          </HomeDemoStack>
        </HomeDemoSection>

        <HomeDemoSection
          title="This week"
          note="4 posts — 2 from Industrial Design, 1 blocker."
          count={thisWeek.length}
          action={{ label: "Full feed", onClick: () => setActiveNav("feed") }}
        >
          <HomeDemoStack>
            {thisWeek.map((item) => (
              <HomeDemoActivityRow key={item.id} item={item} />
            ))}
          </HomeDemoStack>
        </HomeDemoSection>
      </section>

      {/* Projects */}
      <HomeDemoSection
        title="Your projects"
        note="4 active this week, 2 quiet 7+ days."
        count={projects.length}
        action={{ label: "All projects", onClick: () => setActiveNav("projects") }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => (
            <HomeDemoProjectCard key={p.id} project={p} />
          ))}
        </div>
      </HomeDemoSection>

      {/* Needs attention */}
      <HomeDemoSection title="Needs attention" note="4 things drifting.">
        <HomeDemoStack>
          {needsAttention.map((n) => (
            <HomeDemoAttentionRow key={n.id} item={n} />
          ))}
        </HomeDemoStack>
      </HomeDemoSection>
    </div>
  );
}

function HomeDemoLink({ children, strong = false }: { children: React.ReactNode; strong?: boolean }) {
  return (
    <button
      type="button"
      onClick={(e) => e.preventDefault()}
      className={`${strong ? "text-gray-900" : "text-gray-700"} underline decoration-gray-200 hover:decoration-gray-500 underline-offset-${strong ? "4" : "2"} transition-colors`}
    >
      {children}
    </button>
  );
}

function HomeDemoPill({
  children,
  primary = false,
  icon: Icon,
}: {
  children: React.ReactNode;
  primary?: boolean;
  icon: typeof Home;
}) {
  const className = primary
    ? "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-black transition-colors"
    : "inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-colors";
  return (
    <button type="button" className={className}>
      <Icon className={primary ? "h-3.5 w-3.5" : "h-3.5 w-3.5 text-gray-400"} />
      {children}
      {primary && <ArrowRight className="h-3.5 w-3.5" />}
    </button>
  );
}

function HomeDemoSection({
  title,
  note,
  count,
  action,
  children,
}: {
  title: string;
  note?: string;
  count?: number;
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3 gap-4">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-[11px] font-semibold text-gray-700 uppercase tracking-[0.12em]">
            {title}
            {count !== undefined && count > 0 && (
              <span className="ml-2 text-gray-400 normal-case tracking-normal font-normal">{count}</span>
            )}
          </h2>
          {note && <span className="text-xs text-gray-400">{note}</span>}
        </div>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="text-xs text-gray-400 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
          >
            {action.label}
            <ArrowUpRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function HomeDemoStack({ children }: { children: React.ReactNode }) {
  return <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">{children}</div>;
}

function HomeDemoPlateRow({ item }: { item: { kind: string; label: string; sublabel?: string; tone?: string } }) {
  const Icon = item.kind === "standup" ? Calendar : item.kind === "review" ? FileEdit : Circle;
  const iconColor =
    item.tone === "overdue"
      ? "text-red-500"
      : item.tone === "high"
        ? "text-orange-500"
        : item.kind === "standup"
          ? "text-blue-500"
          : item.kind === "review"
            ? "text-purple-500"
            : "text-gray-300";
  return (
    <button type="button" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group w-full text-left">
      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconColor}`} />
      <span className="text-sm text-gray-900 flex-1 min-w-0 truncate">{item.label}</span>
      {item.sublabel && (
        <span
          className={`text-xs flex-shrink-0 ${
            item.tone === "overdue" ? "text-red-500 font-medium" : "text-gray-400"
          }`}
        >
          {item.sublabel}
        </span>
      )}
      <ArrowUpRight className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
}

function HomeDemoActivityRow({
  item,
}: {
  item: { author: string; team: string; project?: string; snippet: string; ago: string; tag?: "blocked" | "at-risk" };
}) {
  return (
    <button type="button" className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group w-full text-left">
      <Clock className="h-3.5 w-3.5 text-gray-300 mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-0.5 flex-wrap">
          <span className="font-medium text-gray-700">{item.author}</span>
          <span className="text-gray-300">·</span>
          <span>{item.team}</span>
          {item.project && (
            <>
              <span className="text-gray-300">·</span>
              <span className="truncate">{item.project}</span>
            </>
          )}
          <span className="text-gray-300">·</span>
          <span>{item.ago}</span>
          {item.tag === "blocked" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
              Blocked
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700 line-clamp-2">{item.snippet}</p>
      </div>
    </button>
  );
}

function HomeDemoProjectCard({ project }: { project: { name: string; team: string; updated: string } }) {
  return (
    <button
      type="button"
      className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors group w-full text-left"
    >
      <Folder className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
        <p className="text-xs text-gray-400 truncate">
          {project.team} · {project.updated}
        </p>
      </div>
      <ArrowUpRight className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
}

function HomeDemoAttentionRow({ item }: { item: { label: string; sublabel?: string } }) {
  return (
    <button type="button" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group w-full text-left">
      <AlertTriangle className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
      <span className="text-sm text-gray-900 flex-1 min-w-0 truncate">{item.label}</span>
      {item.sublabel && <span className="text-xs text-gray-400 flex-shrink-0">{item.sublabel}</span>}
      <ArrowUpRight className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
}

// Feed View
function FeedView() {
  // Dieter's post (newest) will appear as "new"
  const newestPost = mockUpdates[0]; // Dieter Rams
  // Older posts shown immediately
  const olderPosts = mockUpdates.slice(1); // Massimo and Susan

  const [showNewPostBanner, setShowNewPostBanner] = useState(false);
  const [showNewPostExpanded, setShowNewPostExpanded] = useState(false);

  useEffect(() => {
    // After 2 seconds, show "new post" banner
    const bannerTimer = setTimeout(() => {
      setShowNewPostBanner(true);
    }, 2000);

    // After 3.5 seconds, expand to show the full post
    const expandTimer = setTimeout(() => {
      setShowNewPostExpanded(true);
    }, 3500);

    return () => {
      clearTimeout(bannerTimer);
      clearTimeout(expandTimer);
    };
  }, []);

  return (
    <div className="flex gap-6 justify-center">
      {/* Main Feed Area */}
      <div className="flex-1 max-w-2xl">
        {/* Composer */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-start gap-3">
            <UserAvatar
              displayName="Paula Scher"
              email="paula@acme.com"
              size="md"
            />
            <div className="flex-1">
              <div className="text-gray-500 text-sm border border-gray-200 rounded-lg px-4 py-3">
                Share an update, ask a question, or start a discussion...
              </div>
            </div>
          </div>
        </div>

        {/* Updates Feed */}
        <div className="space-y-4">
          {/* New Post Banner / Expanded Post */}
          {showNewPostBanner && !showNewPostExpanded && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 animate-slideUp cursor-pointer hover:bg-blue-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <UserAvatar
                  displayName={newestPost.author.name}
                  email={`${newestPost.author.initials.toLowerCase()}@acme.com`}
                  size="sm"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-blue-900">
                    New post from {newestPost.author.name}
                  </span>
                </div>
                <ChevronRight className="h-4 w-4 text-blue-400" />
              </div>
            </div>
          )}

          {showNewPostExpanded && (
            <UpdateCard update={newestPost} isNew />
          )}

          {/* Older Posts - shown immediately */}
          {olderPosts.map((update) => (
            <UpdateCard key={update.id} update={update} />
          ))}
        </div>
      </div>

      {/* Activity Sidebar */}
      <DemoActivitySidebar />
    </div>
  );
}

// Projects View - matches production layout
function ProjectsView() {
  // Group projects by team
  const projectsByTeam = mockProjects.reduce(
    (acc, project) => {
      if (!acc[project.team]) {
        acc[project.team] = [];
      }
      acc[project.team].push(project);
      return acc;
    },
    {} as Record<string, typeof mockProjects>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <Button size="lg">
            <FolderOpen className="mr-2 h-4 w-4" />
            New Project
          </Button>
        </div>
        <p className="text-gray-600">
          Track and manage all your projects in one place
        </p>
      </div>

      {/* Toolbar - Desktop only */}
      <div className="hidden sm:block py-3 px-4 border-b bg-gray-50/50 rounded-t-lg border border-gray-200 border-b-0">
        <div className="flex items-center gap-6">
          {/* View toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              View
            </span>
            <div className="flex items-center gap-0.5 border rounded-md p-0.5 bg-white">
              <button className="h-7 w-7 flex items-center justify-center rounded bg-gray-900 text-white">
                <Layers className="h-4 w-4" />
              </button>
              <button className="h-7 w-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100">
                <Clock className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="h-6 w-px bg-gray-300" />

          {/* Group toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Group
            </span>
            <div className="flex items-center gap-0.5 border rounded-md p-0.5 bg-white">
              <button className="h-7 px-2.5 flex items-center gap-1.5 rounded bg-gray-900 text-white text-xs">
                <Users className="h-3.5 w-3.5" />
                Team
              </button>
              <button className="h-7 px-2.5 flex items-center gap-1.5 rounded text-gray-500 hover:bg-gray-100 text-xs">
                <Users className="h-3.5 w-3.5" />
                Person
              </button>
            </div>
          </div>

          <div className="h-6 w-px bg-gray-300" />

          {/* Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Filter
            </span>
            <button className="h-7 px-3 flex items-center gap-1.5 rounded border bg-white text-gray-600 text-xs hover:bg-gray-50">
              <CheckCircle2 className="h-3.5 w-3.5" />2 states
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block bg-white border border-gray-200 rounded-b-lg overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div className="col-span-5">Project</div>
          <div className="col-span-2">State</div>
          <div className="col-span-2">Owner</div>
          <div className="col-span-3">Timeline</div>
        </div>

        {/* Grouped Projects */}
        {Object.entries(projectsByTeam).map(([teamName, projects]) => (
          <div key={teamName}>
            {/* Team Header */}
            <div className="px-4 py-2 bg-gray-50/50 border-b flex items-center gap-2">
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="font-medium text-gray-700">{teamName}</span>
              <span className="text-xs text-gray-400">
                {projects.length} project{projects.length > 1 ? "s" : ""}
              </span>
            </div>

            {/* Project Rows */}
            {projects.map((project) => (
              <div
                key={project.id}
                className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50 transition-colors cursor-pointer items-center"
              >
                {/* Project Name */}
                <div className="col-span-5 flex items-center gap-3">
                  <FolderOpen className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900 hover:text-blue-600">
                    {project.name}
                  </span>
                </div>

                {/* State */}
                <div className="col-span-2">
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      project.status === "shipped"
                        ? "bg-green-50 text-green-700"
                        : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    {project.status === "shipped" ? (
                      <>
                        <CheckCircle2 className="h-3 w-3" />
                        Shipped
                      </>
                    ) : (
                      <>
                        <Clock className="h-3 w-3" />
                        Active
                      </>
                    )}
                  </span>
                </div>

                {/* Owner */}
                <div className="col-span-2 flex items-center gap-2">
                  <UserAvatar
                    displayName={project.lead.name}
                    email={`${project.lead.initials.toLowerCase()}@acme.com`}
                    size="xs"
                  />
                  <span className="text-sm text-gray-600 truncate">
                    {project.lead.name.split(" ")[0]}
                  </span>
                </div>

                {/* Timeline */}
                <div className="col-span-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          project.status === "shipped"
                            ? "bg-green-500 w-full"
                            : "bg-blue-500 w-3/4"
                        }`}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {project.status === "shipped" ? "Done" : "Q4 2024"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-4 mt-4">
        {Object.entries(projectsByTeam).map(([teamName, projects]) => (
          <div key={teamName}>
            {/* Team Header */}
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-gray-900">{teamName}</span>
              <span className="text-xs text-gray-400">
                {projects.length} project{projects.length > 1 ? "s" : ""}
              </span>
            </div>

            {/* Project Cards */}
            <div className="space-y-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {project.name}
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                        project.status === "shipped"
                          ? "bg-green-50 text-green-700"
                          : "bg-blue-50 text-blue-700"
                      }`}
                    >
                      {project.status === "shipped" ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          Shipped
                        </>
                      ) : (
                        <>
                          <Clock className="h-3 w-3" />
                          Active
                        </>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        displayName={project.lead.name}
                        email={`${project.lead.initials.toLowerCase()}@acme.com`}
                        size="sm"
                      />
                      <span className="text-sm text-gray-600">
                        {project.lead.name}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            project.status === "shipped"
                              ? "bg-green-500 w-full"
                              : "bg-blue-500 w-3/4"
                          }`}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {project.status === "shipped" ? "Done" : "Q4 2024"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Teams View - matches production layout
function TeamsView() {
  // Icon mapping
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    Compass,
    Map,
    Monitor,
    Palette,
    Users,
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold">Teams</h1>
          <Button size="lg">
            <Plus className="mr-2 h-4 w-4" />
            New Team
          </Button>
        </div>
        <p className="text-gray-600">
          Organize your workspace into focused teams that work on specific areas
        </p>
      </div>

      {/* Team Cards Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {mockTeams.map((team) => {
          const IconComponent = iconMap[team.icon] || Users;

          return (
            <div
              key={team.id}
              className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all cursor-pointer"
            >
              {/* Team Header with Gradient */}
              <div
                className="h-24 relative"
                style={{
                  background: `linear-gradient(to bottom right, ${team.gradient.from}, ${team.gradient.to})`,
                }}
              >
                <div className="absolute inset-0 bg-black/10" />
                <div className="absolute top-3 right-3">
                  {team.membershipType === "open" ? (
                    <div className="bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
                      <Globe className="h-3 w-3" />
                      Open
                    </div>
                  ) : team.membershipType === "private" ? (
                    <div className="bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
                      <EyeOff className="h-3 w-3" />
                      Private
                    </div>
                  ) : (
                    <div className="bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
                      <Lock className="h-3 w-3" />
                      Closed
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-6 left-4">
                  <div className="w-14 h-14 bg-white rounded-xl shadow-md flex items-center justify-center">
                    <IconComponent className="h-7 w-7 text-indigo-600" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="pt-10 pb-4 px-4">
                {/* Team Name & Description */}
                <div className="mb-4">
                  <h3 className="font-semibold text-lg text-gray-900 group-hover:text-indigo-600 transition-colors flex items-center gap-2">
                    {team.name}
                    <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {team.description}
                  </p>
                </div>

                {/* Stats Row */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      <span>{team.members.length}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FolderOpen className="h-3.5 w-3.5" />
                      <span>{team.projects}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Activity className="h-3.5 w-3.5" />
                    <span className="text-xs">
                      {team.activeProjects} active
                    </span>
                  </div>
                </div>

                {/* Member Facepile */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {team.members.map((member, index) => (
                        <div key={`${team.id}-member-${index}`} className="relative">
                          <UserAvatar
                            displayName={member.name}
                            email={`${member.initials.toLowerCase()}@acme.com`}
                            size="sm"
                            className="ring-2 ring-white"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Create New Team Card */}
        <div className="group bg-white rounded-xl shadow-sm border-2 border-dashed border-gray-200 overflow-hidden hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-center min-h-[320px]">
          <div className="text-center p-6">
            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-50 transition-colors">
              <Plus className="h-7 w-7 text-gray-400 group-hover:text-indigo-600 transition-colors" />
            </div>
            <h3 className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
              Create New Team
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Organize members and projects
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Workflows View - matches production layout
function WorkflowsView() {
  const [activeTab, setActiveTab] = useState("all");
  const [view, setView] = useState<"grid" | "list">("list");

  // Workflow type icons and colors
  const workflowTypeConfig: Record<
    string,
    { icon: React.ComponentType<{ className?: string }>; color: string; label: string }
  > = {
    standup: { icon: MessageSquare, color: "text-blue-600 bg-blue-50", label: "Standup" },
    rollup: { icon: RefreshCw, color: "text-purple-600 bg-purple-50", label: "Rollup" },
    crit: { icon: FileEdit, color: "text-orange-600 bg-orange-50", label: "Crit" },
    retro: { icon: RotateCcw, color: "text-green-600 bg-green-50", label: "Retro" },
  };

  // Status badge config
  const statusConfig: Record<string, { color: string; label: string }> = {
    active: { color: "bg-green-50 text-green-700", label: "Active" },
    draft: { color: "bg-gray-100 text-gray-600", label: "Draft" },
    published: { color: "bg-blue-50 text-blue-700", label: "Published" },
  };

  // Filter tabs
  const tabs = [
    { id: "all", label: "All" },
    { id: "mine", label: "Mine" },
    { id: "team", label: "Team" },
    { id: "published", label: "Published" },
  ];

  // Filter workflows based on active tab
  const filteredWorkflows = mockWorkflows.filter((w) => {
    if (activeTab === "all") return true;
    if (activeTab === "mine") return w.owner.initials === "PS"; // Paula Scher
    if (activeTab === "team") return w.teamId !== "all";
    if (activeTab === "published") return w.status === "published";
    return true;
  });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold">Workflows</h1>
          <Button size="lg">
            <Plus className="mr-2 h-4 w-4" />
            New Workflow
          </Button>
        </div>
        <p className="text-gray-600">
          Manage your rollups, standups, crits, and retros in one place
        </p>
      </div>

      {/* Filters - Desktop */}
      <div className="hidden sm:block mb-6">
        <div className="flex items-center justify-between">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-0.5 border rounded-md p-0.5 bg-white">
            <button
              onClick={() => setView("list")}
              className={`h-7 w-7 flex items-center justify-center rounded ${
                view === "list" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView("grid")}
              className={`h-7 w-7 flex items-center justify-center rounded ${
                view === "grid" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Workflow List/Grid */}
      {view === "list" ? (
        <>
          {/* Desktop Table */}
          <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-4">Workflow</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Owner</div>
              <div className="col-span-2">Activity</div>
            </div>

            {/* Workflow Rows */}
            {filteredWorkflows.map((workflow) => {
              const typeConfig = workflowTypeConfig[workflow.type];
              const TypeIcon = typeConfig.icon;
              const status = statusConfig[workflow.status];

              return (
                <div
                  key={workflow.id}
                  className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50 transition-colors cursor-pointer items-center"
                >
                  {/* Workflow Name */}
                  <div className="col-span-4 flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${typeConfig.color.split(" ")[1]}`}>
                      <TypeIcon className={`h-4 w-4 ${typeConfig.color.split(" ")[0]}`} />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 hover:text-blue-600">
                        {workflow.title}
                      </span>
                      <div className="text-xs text-gray-500">{workflow.team}</div>
                    </div>
                  </div>

                  {/* Type */}
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Owner */}
                  <div className="col-span-2 flex items-center gap-2">
                    <UserAvatar
                      displayName={workflow.owner.name}
                      email={`${workflow.owner.initials.toLowerCase()}@acme.com`}
                      size="xs"
                    />
                    <span className="text-sm text-gray-600 truncate">
                      {workflow.owner.name.split(" ")[0]}
                    </span>
                  </div>

                  {/* Activity */}
                  <div className="col-span-2 text-sm text-gray-500">
                    {workflow.lastActivity}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden space-y-3">
            {filteredWorkflows.map((workflow) => {
              const typeConfig = workflowTypeConfig[workflow.type];
              const TypeIcon = typeConfig.icon;
              const status = statusConfig[workflow.status];

              return (
                <div
                  key={workflow.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${typeConfig.color.split(" ")[1]}`}>
                      <TypeIcon className={`h-5 w-5 ${typeConfig.color.split(" ")[0]}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{workflow.title}</h3>
                      <p className="text-sm text-gray-500">{workflow.team}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        displayName={workflow.owner.name}
                        email={`${workflow.owner.initials.toLowerCase()}@acme.com`}
                        size="sm"
                      />
                      <span className="text-sm text-gray-600">
                        {workflow.owner.name}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">{workflow.lastActivity}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredWorkflows.map((workflow) => {
            const typeConfig = workflowTypeConfig[workflow.type];
            const TypeIcon = typeConfig.icon;
            const status = statusConfig[workflow.status];

            return (
              <div
                key={workflow.id}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${typeConfig.color.split(" ")[1]}`}>
                    <TypeIcon className={`h-5 w-5 ${typeConfig.color.split(" ")[0]}`} />
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                    {status.label}
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 mb-1">{workflow.title}</h3>
                <p className="text-sm text-gray-500 mb-3">{workflow.team}</p>

                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      displayName={workflow.owner.name}
                      email={`${workflow.owner.initials.toLowerCase()}@acme.com`}
                      size="xs"
                    />
                    <span className="text-sm text-gray-600">
                      {workflow.owner.name.split(" ")[0]}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{workflow.lastActivity}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Notifications View
function NotificationsView() {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-600 mt-1">Stay updated on your team's work</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {mockNotifications.map((notification, idx) => (
          <div
            key={notification.id}
            className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
              idx !== mockNotifications.length - 1
                ? "border-b border-gray-100"
                : ""
            } ${notification.unread ? "bg-amber-50/50" : ""}`}
          >
            <div className="flex items-start gap-3">
              <UserAvatar
                displayName={notification.actor.name}
                email={`${notification.actor.initials.toLowerCase()}@acme.com`}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800">
                  <span className="font-medium">{notification.actor.name}</span>{" "}
                  {notification.action}{" "}
                  <span className="font-medium">{notification.target}</span>
                </p>
                <span className="text-xs text-gray-500">
                  {notification.time}
                </span>
              </div>
              {notification.unread && (
                <div className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0 mt-2" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoFeedContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Fire demo_viewed once per page load so we can measure this Facebook/social
  // acquisition surface as a funnel step, not just as a raw pageview (LB-289).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ph = (window as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    ph?.capture?.("demo_viewed", {
      source: "demo_feed",
      referrer_url: document.referrer || null,
      entry_point: window.location.pathname + window.location.search,
    });
  }, []);

  // Get active nav from URL query param, default to "home"
  const viewParam = searchParams.get("view") as NavItem | null;
  const activeNav: NavItem = viewParam && ["home", "feed", "projects", "teams", "workflows", "notifications"].includes(viewParam)
    ? viewParam
    : "home";

  // Update URL when nav changes and notify parent window
  const setActiveNav = useCallback((nav: NavItem) => {
    if (nav === "home") {
      router.push("/demo/feed", { scroll: false });
    } else {
      router.push(`/demo/feed?view=${nav}`, { scroll: false });
    }

    // Notify parent window (marketing page) of nav change
    if (window.parent !== window) {
      window.parent.postMessage({ type: "demo-nav-change", view: nav }, "*");
    }
  }, [router]);

  const renderContent = () => {
    switch (activeNav) {
      case "home":
        return <HomeView setActiveNav={setActiveNav} />;
      case "feed":
        return <FeedView />;
      case "projects":
        return <ProjectsView />;
      case "teams":
        return <TeamsView />;
      case "workflows":
        return <WorkflowsView />;
      case "notifications":
        return <NotificationsView />;
      default:
        return <HomeView setActiveNav={setActiveNav} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative pb-20 sm:pb-0">
      {/* Demo → signup conversion CTA (LB-120) */}
      <DemoSignupBadge />

      {/* Desktop Sidebar */}
      <DemoSidebar activeNav={activeNav} setActiveNav={setActiveNav} />

      {/* Mobile Bottom Nav */}
      <MobileBottomNav activeNav={activeNav} setActiveNav={setActiveNav} />

      {/* Main Content */}
      <div className="p-4 sm:p-8 sm:pl-24">
        <div className="max-w-6xl mx-auto">{renderContent()}</div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

function DemoSignupBadge() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ph = (window as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    ph?.capture?.("demo_cta_banner_viewed", { location: "demo_feed_badge" });
  }, []);

  const handleClick = () => {
    if (typeof window === "undefined") return;
    const ph = (window as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    ph?.capture?.("demo_cta_banner_clicked", { location: "demo_feed_badge" });
    ph?.capture?.("demo_signup_clicked", { location: "demo_feed_badge" });
  };

  return (
    <a
      href="/auth"
      onClick={handleClick}
      className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-1 ring-black/5 hover:bg-gray-800 transition-colors"
    >
      <span className="hidden sm:inline text-xs font-normal text-white/70">Demo mode</span>
      <span className="hidden sm:inline text-white/30">·</span>
      <span>Sign up free</span>
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

export default function DemoFeedPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-gray-50" />}>
      <DemoFeedContent />
    </Suspense>
  );
}
